import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Collapse,
  Typography,
} from "@mui/material";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import TimerOffOutlinedIcon from "@mui/icons-material/TimerOffOutlined";
import inkfreeLogo from "assets/images/signer_inkfree_logo.svg";
import strings from "global/constants/StringConstants";
import { fillCopy, getErrorMessageText } from "helpers/methods";
import {
  IDV_METHODS,
  currentIdvMethod,
  idvAttemptsLeftLabel,
  idvFailureHint,
  isIdvAlreadyVerifiedError,
  isIdvBlockedError,
  isIdvStartingError,
  needsSignerIdv,
} from "helpers/identityVerificationHelpers";
import {
  IdvMethod,
  IdvSession,
  IdvTestOutcome,
  SignerIdvState,
} from "types/identityVerification";
import { getApiStatus } from "utils/apiStatus";
import { white } from "theme/colors";
import {
  checkIdentityVerification,
  fetchSignerIdentityVerification,
  startIdentityVerification,
  submitIdentityVerificationTestOutcome,
} from "../ParticipantScreenService";
import SignerCard from "./SignerCard";
import {
  CARD,
  IDV,
  StatusVisual,
  amber,
  green,
  purple,
  red,
} from "./SignerStatusCard.tokens";

const copy = strings.identityVerification;

/** §3.3: poll every 2s for 2 minutes, then every 10s with the "still checking" note. */
const POLL_FAST_MS = 2000;
const POLL_SLOW_MS = 10000;
const FAST_POLL_WINDOW_MS = 2 * 60 * 1000;
/** An `IDV_STARTING:` 409 is a double-click race; one retry after a beat. */
const START_RETRY_DELAY_MS = 800;

type Phase =
  | "loading"
  | "intro"
  | "launch"
  | "redirecting"
  | "checking"
  | "methodPassed"
  | "passed"
  | "failed"
  | "blocked"
  | "timedOut";

/** Where a fresh (no open session) state lands. */
const restingPhase = (state?: SignerIdvState | null): Phase => {
  if (!state) return "loading";
  if (!needsSignerIdv(state)) return "passed";
  if (state.status === "BLOCKED") return "blocked";
  if (currentIdvMethod(state)?.status === "FAILED") return "failed";
  return "intro";
};

const PHASE_VISUAL: Record<
  Phase,
  {
    visual: Pick<StatusVisual, "strip" | "circle" | "circleShadow">;
    Icon: typeof BadgeOutlinedIcon;
  }
> = {
  loading: { visual: purple, Icon: BadgeOutlinedIcon },
  intro: { visual: purple, Icon: BadgeOutlinedIcon },
  launch: { visual: purple, Icon: BadgeOutlinedIcon },
  redirecting: { visual: purple, Icon: BadgeOutlinedIcon },
  checking: { visual: purple, Icon: HourglassEmptyRoundedIcon },
  methodPassed: { visual: green, Icon: CheckRoundedIcon },
  passed: { visual: green, Icon: CheckRoundedIcon },
  failed: { visual: amber, Icon: ErrorOutlineRoundedIcon },
  timedOut: { visual: amber, Icon: TimerOffOutlinedIcon },
  blocked: { visual: red, Icon: LockOutlinedIcon },
};

interface Props {
  reqToken: string;
  /** `signerIdentityVerification` from the token fetch; fetched here when absent. */
  initialState?: SignerIdvState;
  senderName?: string;
  /** Every method passed: re-fetch the request and open the document. */
  onVerified: () => void | Promise<void>;
}

/**
 * Signer identity verification — shown INSTEAD of the document (after any
 * access code / OTP step) until every required method has passed. One card
 * for desktop and mobile, like the other signer status cards.
 *
 * Flow: start a session → launch it per `launch.type` (TEST buttons / REDIRECT
 * to the vendor / EMBED placeholder) → poll until the method resolves → next
 * method, or done. Failure, lock-out and session time-out each get their own
 * card; a timed-out session never uses up an attempt.
 */
const IdentityVerificationContent = ({
  reqToken,
  initialState,
  senderName,
  onVerified,
}: Props) => {
  const [idv, setIdv] = useState<SignerIdvState | undefined>(initialState);
  const [phase, setPhase] = useState<Phase>(restingPhase(initialState));
  const [session, setSession] = useState<IdvSession | null>(null);
  const [activeMethod, setActiveMethod] = useState<IdvMethod | null>(null);
  const [stillChecking, setStillChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [whyOpen, setWhyOpen] = useState(false);

  const mountedRef = useRef(true);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const stopTimers = useCallback(() => {
    clearTimeout(pollTimerRef.current);
  }, []);

  useEffect(
    () => () => {
      mountedRef.current = false;
      stopTimers();
    },
    [stopTimers],
  );

  /** Best-effort re-read (attempts left, fallen-back status) — never blocks a card. */
  const refreshState = useCallback(async () => {
    try {
      const next = await fetchSignerIdentityVerification(reqToken);
      if (mountedRef.current) setIdv(next);
      return next;
    } catch {
      return undefined;
    }
  }, [reqToken]);

  /**
   * Routes a state answer to its card. Returns true while the session's method
   * is still being checked (the caller keeps polling).
   */
  const applyState = useCallback(
    (next: SignerIdvState, method: IdvMethod | null): boolean => {
      setIdv(next);
      setError("");
      const settle = (to: Phase) => {
        stopTimers();
        setSession(null);
        setStillChecking(false);
        setPhase(to);
        return false;
      };
      if (!needsSignerIdv(next)) return settle("passed");
      if (next.status === "BLOCKED") return settle("blocked");
      // The polled session was dropped unfinished (timed out or replaced);
      // the attempt wasn't counted.
      if (next.sessionExpired) return settle("timedOut");
      const current =
        (method && next.methods.find((m) => m.method === method)) ||
        currentIdvMethod(next);
      switch (current?.status) {
        case "PASSED":
          return settle("methodPassed");
        case "IN_PROGRESS":
          setPhase("checking");
          return true;
        case "FAILED":
          return settle("failed");
        default:
          return settle(restingPhase(next));
      }
    },
    [stopTimers],
  );

  const startPolling = useCallback(
    (sessionId: string, method: IdvMethod | null) => {
      clearTimeout(pollTimerRef.current);
      const startedAt = Date.now();
      setStillChecking(false);
      setPhase("checking");
      const tick = async () => {
        if (!mountedRef.current) return;
        try {
          const next = await checkIdentityVerification(reqToken, sessionId);
          if (!mountedRef.current) return;
          if (!applyState(next, method)) return;
        } catch (err: any) {
          if (!mountedRef.current) return;
          // 404: the session isn't this signer's. Stop; offer a fresh start.
          if (getApiStatus(err) === 404) {
            stopTimers();
            setSession(null);
            setStillChecking(false);
            setPhase("intro");
            setError(copy.startError);
            return;
          }
          // Anything else is treated as transient — keep polling.
        }
        const slow = Date.now() - startedAt >= FAST_POLL_WINDOW_MS;
        if (slow) setStillChecking(true);
        pollTimerRef.current = setTimeout(
          tick,
          slow ? POLL_SLOW_MS : POLL_FAST_MS,
        );
      };
      tick();
    },
    [applyState, reqToken, stopTimers],
  );

  // First paint: fetch the state if the token fetch didn't carry it, and pick
  // up the signer's still-open session (`openSessionId`): a REDIRECT round
  // trip to the vendor, another tab, or another device.
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      const state = initialState ?? (await refreshState());
      if (cancelled || !mountedRef.current) return;
      if (!state) {
        setPhase("intro");
        return;
      }
      setIdv(state);
      if (
        state.openSessionId &&
        needsSignerIdv(state) &&
        state.status !== "BLOCKED"
      ) {
        const method = currentIdvMethod(state)?.method ?? null;
        setActiveMethod(method);
        startPolling(state.openSessionId, method);
        return;
      }
      setPhase(restingPhase(state));
    };
    init();
    return () => {
      cancelled = true;
    };
    // Mount only: later state changes come through applyState.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async (retried = false): Promise<void> => {
    setBusy(true);
    setError("");
    const method = idv?.nextMethod ?? currentIdvMethod(idv)?.method ?? null;
    try {
      const next = await startIdentityVerification(reqToken, method);
      if (!mountedRef.current) return;
      setSession(next);
      setActiveMethod(method);
      if (next.launch?.type === "REDIRECT" && next.launch.url) {
        // The returning page resumes from `openSessionId`; nothing to park.
        setPhase("redirecting");
        window.location.assign(next.launch.url);
        return;
      }
      // TEST shows the simulate buttons; EMBED the vendor placeholder.
      setPhase("launch");
    } catch (err: any) {
      if (!mountedRef.current) return;
      const message = getErrorMessageText(err);
      if (isIdvBlockedError(err)) {
        setPhase("blocked");
        refreshState();
      } else if (isIdvAlreadyVerifiedError(err)) {
        setPhase("passed");
      } else if (isIdvStartingError(err) && !retried) {
        await new Promise((resolve) =>
          setTimeout(resolve, START_RETRY_DELAY_MS),
        );
        // Awaited so `finally` below doesn't clear `busy` mid-retry.
        await start(true);
        return;
      } else {
        setError(message || copy.startError);
      }
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  const submitTestOutcome = async (outcome: IdvTestOutcome) => {
    if (!session) return;
    setBusy(true);
    setError("");
    try {
      const next = await submitIdentityVerificationTestOutcome(
        reqToken,
        session.sessionId,
        outcome,
      );
      if (!mountedRef.current) return;
      if (applyState(next, activeMethod)) {
        startPolling(session.sessionId, activeMethod);
      }
    } catch (err: any) {
      if (mountedRef.current) {
        setError(getErrorMessageText(err) || copy.startError);
      }
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  const finish = async () => {
    setBusy(true);
    try {
      await onVerified();
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  // ------------------------------------------------------------- copy bits

  const orderedMethods = IDV_METHODS.map((method) =>
    idv?.methods?.find((m) => m.method === method),
  ).filter((m): m is NonNullable<typeof m> => !!m);
  const current = currentIdvMethod(idv);
  const stepMethod = activeMethod ?? current?.method ?? null;
  const stepIndex = orderedMethods.findIndex((m) => m.method === stepMethod);
  const stepLabel =
    stepMethod && orderedMethods.length > 0
      ? fillCopy(copy.stepLabel, {
          step: stepIndex + 1,
          total: orderedMethods.length,
          method: copy.signerMethods[stepMethod],
        })
      : "";
  const withSender = (withName: string, without: string) =>
    senderName ? fillCopy(withName, { sender: senderName }) : without;
  const pending = orderedMethods.filter((m) => m.status !== "PASSED");

  const primary = (label: string, onClick: () => void) => (
    <Button
      variant="contained"
      size="large"
      onClick={onClick}
      disabled={busy}
      startIcon={
        busy ? <CircularProgress size={18} color="inherit" /> : undefined
      }
      sx={{ minWidth: 200 }}
    >
      {label}
    </Button>
  );

  const note = (text: string) => (
    <Typography
      sx={{ fontSize: IDV.noteFontSize, color: IDV.noteColor, lineHeight: 1.5 }}
    >
      {text}
    </Typography>
  );

  // ------------------------------------------------------------- per phase

  let title = "";
  let body = "";
  let content: ReactNode = null;

  switch (phase) {
    case "loading":
      content = <CircularProgress size={28} />;
      break;

    case "intro":
      title = copy.introTitle;
      body = withSender(copy.introBody, copy.introBodyNoSender);
      content = (
        <>
          {orderedMethods.length > 0 && (
            <Box
              component="ol"
              aria-label={copy.introTitle}
              sx={{
                listStyle: "none",
                m: 0,
                width: "100%",
                boxSizing: "border-box",
                px: `${IDV.stepsPadX}px`,
                py: `${IDV.stepsPadY}px`,
                bgcolor: IDV.stepsBg,
                border: `1px solid ${IDV.stepsBorder}`,
                borderRadius: IDV.radius,
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                textAlign: "left",
              }}
            >
              {orderedMethods.map((m, index) => (
                <Box
                  component="li"
                  key={m.method}
                  sx={{ display: "flex", alignItems: "center", gap: 1.25 }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      width: IDV.stepNumSize,
                      height: IDV.stepNumSize,
                      flexShrink: 0,
                      borderRadius: "50%",
                      bgcolor: IDV.stepNumBg,
                      color: IDV.stepNumColor,
                      fontSize: IDV.noteFontSize,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {m.status === "PASSED" ? (
                      <CheckRoundedIcon sx={{ fontSize: 14 }} />
                    ) : (
                      index + 1
                    )}
                  </Box>
                  <Typography
                    sx={{ fontSize: IDV.stepFontSize, color: IDV.stepColor }}
                  >
                    {copy.signerMethods[m.method]}
                    {m.status === "PASSED" &&
                      ` (${copy.methodStatuses.PASSED.toLowerCase()})`}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          {pending.length > 0 && (
            <Box sx={{ width: "100%", textAlign: "left" }}>
              <Typography
                sx={{
                  fontSize: IDV.stepFontSize,
                  fontWeight: 700,
                  color: IDV.stepColor,
                  mb: 0.5,
                }}
              >
                {copy.needTitle}
              </Typography>
              {pending.some((m) => m.method === "ID_DOCUMENT") &&
                note(copy.needIdDocument)}
              {pending.some((m) => m.method === "KBA") && note(copy.needKba)}
            </Box>
          )}
          {primary(copy.start, () => start())}
          <ButtonBase
            disableRipple
            onClick={() => setWhyOpen((open) => !open)}
            aria-expanded={whyOpen}
            aria-controls="idv-why"
            sx={{
              fontSize: IDV.noteFontSize,
              color: IDV.stepNumColor,
              fontWeight: 600,
              textDecoration: "underline",
            }}
          >
            {copy.whyLink}
          </ButtonBase>
          <Collapse in={whyOpen} id="idv-why">
            {note(copy.whyBody)}
          </Collapse>
        </>
      );
      break;

    case "launch":
      title = copy.introTitle;
      body = stepLabel;
      content =
        session?.launch?.type === "TEST" ? (
          <Box
            sx={{
              width: "100%",
              boxSizing: "border-box",
              p: 2,
              borderRadius: IDV.radius,
              bgcolor: IDV.testBg,
              border: `1px solid ${IDV.testBorder}`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Typography
              sx={{
                fontSize: IDV.stepFontSize,
                fontWeight: 700,
                color: IDV.testTitle,
              }}
            >
              {copy.testModeTitle}
            </Typography>
            {note(copy.testModeBody)}
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <Button
                variant="contained"
                disabled={busy}
                onClick={() => submitTestOutcome("PASS")}
              >
                {copy.simulatePass}
              </Button>
              <Button
                variant="outlined"
                disabled={busy}
                onClick={() => submitTestOutcome("FAIL")}
              >
                {copy.simulateFail}
              </Button>
            </Box>
          </Box>
        ) : (
          // EMBED: the vendor web SDK mounts here once a vendor is chosen.
          <Box
            role="region"
            aria-label={copy.partnerTitle}
            sx={{
              width: "100%",
              boxSizing: "border-box",
              minHeight: IDV.partnerMinHeight,
              p: 2,
              borderRadius: IDV.radius,
              border: `1px dashed ${IDV.partnerBorder}`,
              bgcolor: IDV.partnerBg,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: IDV.stepFontSize,
                fontWeight: 700,
                color: IDV.stepColor,
              }}
            >
              {copy.partnerTitle}
            </Typography>
            {note(copy.embedPlaceholder)}
          </Box>
        );
      break;

    case "redirecting":
      title = copy.introTitle;
      body = copy.redirecting;
      content = <CircularProgress size={28} />;
      break;

    case "checking":
      title = copy.checkingTitle;
      body = stillChecking ? copy.stillChecking : copy.checkingBody;
      content = <CircularProgress size={28} />;
      break;

    case "methodPassed": {
      const next = currentIdvMethod(idv);
      title = copy.methodPassedTitle;
      body = next
        ? fillCopy(copy.methodPassedBody, {
            method: copy.signerMethods[next.method],
          })
        : "";
      content = primary(copy.continueLabel, () => start());
      break;
    }

    case "passed":
      title = copy.passedTitle;
      body = copy.passedBody;
      content = primary(copy.continueToDocument, finish);
      break;

    case "failed":
      title = copy.failedTitle;
      body = idvFailureHint(current?.failureReason);
      content = (
        <>
          {typeof current?.attemptsLeft === "number" &&
            note(idvAttemptsLeftLabel(current.attemptsLeft))}
          {primary(copy.tryAgain, () => start())}
        </>
      );
      break;

    case "blocked":
      title = copy.blockedTitle;
      body = withSender(copy.blockedBody, copy.blockedBodyNoSender);
      break;

    case "timedOut":
      title = copy.timedOutTitle;
      body = copy.timedOutBody;
      content = primary(copy.startAgain, () => start());
      break;
  }

  const { visual, Icon } = PHASE_VISUAL[phase];

  return (
    <SignerCard
      maxWidth={CARD.width}
      strip={visual.strip}
      sx={{ boxShadow: CARD.shadow }}
    >
      <Box
        sx={{
          px: { xs: 3.5, sm: `${CARD.bodyPadX}px` },
          pt: `${CARD.bodyPadTop}px`,
          pb: `${CARD.bodyPadTop}px`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <Box
          component="img"
          src={inkfreeLogo}
          alt="Inkfree"
          sx={{ width: CARD.logoWidth, height: "auto" }}
        />
        <Box
          aria-hidden
          sx={{
            mt: `${CARD.circleGap}px`,
            width: CARD.circleSize,
            height: CARD.circleSize,
            borderRadius: "50%",
            background: visual.circle,
            boxShadow: visual.circleShadow,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon sx={{ fontSize: IDV.glyphSize, color: white }} />
        </Box>
        {/* Polite live region: each step change is announced. */}
        <Box aria-live="polite" sx={{ width: "100%" }}>
          {title && (
            <Typography
              component="h2"
              sx={{
                mt: `${CARD.titleGap}px`,
                fontSize: CARD.titleSize,
                fontWeight: 800,
                color: CARD.titleColor,
                letterSpacing: "-0.01em",
                lineHeight: 1.25,
              }}
            >
              {title}
            </Typography>
          )}
          {body && (
            <Typography
              sx={{
                mt: `${CARD.bodyGap}px`,
                mx: "auto",
                fontSize: CARD.bodySize,
                lineHeight: CARD.bodyLineHeight,
                color: CARD.bodyColor,
                maxWidth: CARD.bodyMaxWidth,
              }}
            >
              {body}
            </Typography>
          )}
        </Box>
        {(content || error) && (
          <Box
            sx={{
              mt: `${IDV.sectionGap}px`,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            {content}
            {error && (
              <Typography variant="body2" color="error" role="alert">
                {error}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </SignerCard>
  );
};

export default IdentityVerificationContent;
